/**
 * Notification system enums - aligned with backend Alaric.Services.Messaging
 */

/**
 * Specifies the available notification channels.
 */
export enum NotificationChannel {
    Email = 0,
    Sms = 1,
    Push = 2,
    Pull = 3, // In-app notifications
}

/**
 * Channel display labels for UI
 */
export const NotificationChannelLabels: Record<NotificationChannel, string> = {
    [NotificationChannel.Email]: "Email",
    [NotificationChannel.Sms]: "SMS",
    [NotificationChannel.Push]: "Push",
    [NotificationChannel.Pull]: "In-App",
};

/**
 * Get channel options for dropdowns
 */
export const getChannelOptions = () =>
    Object.entries(NotificationChannelLabels).map(([value, label]) => ({
        value: Number(value) as NotificationChannel,
        label,
    }));

/**
 * Specifies the status of a notification template.
 */
export enum TemplateStatus {
    Draft = 0,
    Active = 1,
    Disabled = 2,
}

/**
 * Template status display labels for UI
 */
export const TemplateStatusLabels: Record<TemplateStatus, string> = {
    [TemplateStatus.Draft]: "Draft",
    [TemplateStatus.Active]: "Active",
    [TemplateStatus.Disabled]: "Disabled",
};

/**
 * Get status options for dropdowns
 */
export const getStatusOptions = () =>
    Object.entries(TemplateStatusLabels).map(([value, label]) => ({
        value: Number(value) as TemplateStatus,
        label,
    }));

/**
 * Specifies the priority level for push notifications.
 * High priority notifications bypass doze mode and are delivered immediately.
 */
export enum NotificationPriority {
    Normal = 0,
    High = 1,
}

/**
 * Priority display labels for UI
 */
export const NotificationPriorityLabels: Record<NotificationPriority, string> = {
    [NotificationPriority.Normal]: "Normal",
    [NotificationPriority.High]: "High",
};

/**
 * Get priority options for dropdowns
 */
export const getPriorityOptions = () =>
    Object.entries(NotificationPriorityLabels).map(([value, label]) => ({
        value: Number(value) as NotificationPriority,
        label,
    }));

/**
 * Specifies the SMS message type, used by providers to classify traffic.
 */
export enum SmsMessageType {
    Otp = 0,
    Transactional = 1,
}

/**
 * SMS message type display labels for UI
 */
export const SmsMessageTypeLabels: Record<SmsMessageType, string> = {
    [SmsMessageType.Otp]: "OTP / Verification Code",
    [SmsMessageType.Transactional]: "Transactional / Alert",
};

/**
 * Get SMS message type options for dropdowns
 */
export const getSmsMessageTypeOptions = () =>
    Object.entries(SmsMessageTypeLabels).map(([value, label]) => ({
        value: Number(value) as SmsMessageType,
        label,
    }));

/**
 * Specifies the delivery status of a notification.
 */
export enum DeliveryStatus {
    Queued = 0,
    Sent = 1,
    Failed = 2,
    Delivered = 3,
    Read = 4,
}

/**
 * Delivery status display labels for UI
 */
export const DeliveryStatusLabels: Record<DeliveryStatus, string> = {
    [DeliveryStatus.Queued]: "Queued",
    [DeliveryStatus.Sent]: "Sent",
    [DeliveryStatus.Failed]: "Failed",
    [DeliveryStatus.Delivered]: "Delivered",
    [DeliveryStatus.Read]: "Read",
};
